import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { View, Text, Platform, Dimensions, RefreshControl, Animated, Image, TextInput, useWindowDimensions, FlatList } from 'react-native';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { Title } from '@constants/Themed';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useQuery } from '@tanstack/react-query';
import apis from '@api/api';
import { filter } from 'lodash';
import { FontAwesome, FontAwesome5, SimpleLineIcons } from '@expo/vector-icons';
import AnimatedLottieView from '@utils/lottieAnimation';
import GlobalTutorial from '@components/TutorialComponent/GlobalTutorial';
import { carouselData } from './Tutorial/CarouselData';
import styles from '@stylesheet';
import PreviousCampaignItem from './Components/PrevCampaignItem';
import { UserInfoModalHandle } from '../ProjectManagementScreen/Components/OwnerInfoModal';
import { ShareConfirmHandle } from '@components/modals/ShareUserModal/ShareUserModal';
import BottomSheet from '@gorhom/bottom-sheet';

const PreviousCampaigns = ({ navigation }) => {
  const refreshTracker = useSelector((state: DefaultRootState) => state.app.refreshTracker);
  const [refresh, setRefresh] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const [columns, setColumns] = useState(0);
  const [newCarouselData, setNewCarouselData] = useState(carouselData);

  const { width } = useWindowDimensions();
  const [listHorizontal, setListHorizontal] = useState(Platform.OS === 'web' && width > 1600);

  const calculateColumns = async () => {
    const columns = Math.floor(width / 350);
    setColumns(columns);
  }

  useEffect(() => {
    refetch()
  }, [refreshTracker])

  useLayoutEffect(() => {
    if (width > 1600) {
      calculateColumns();
      setListHorizontal(true);
    } else {
      setListHorizontal(false);
      setColumns(1);
    }
  }, [width])

  const { status, data, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['expiredCampaigns'],
    queryFn: async () => {
      const data = await apis.getExpiredCampaigns()
      return data
    },
  })

  useEffect(() => {
    if (isFetching) {
      const loadingTut = { title: "Loading...", caption: "Fetching previous campaigns", image: <AnimatedLottieView loop style={{ width: 100, flex: 1 }} source={require('@assets/animations/leaf_Loader.json')} /> }
      const modded = [loadingTut, ...newCarouselData]
      setNewCarouselData(modded);
    } else {
      if (newCarouselData[0]?.title === "Loading...") {
        const modded = newCarouselData.slice(1);
        setNewCarouselData(modded);
      }
    }
  }, [isFetching])

  useEffect(() => { return () => { bottomDrawerRef.current?.forceClose() } }, [])

  const [query, setQuery] = useState('');
  const bottomDrawerRef = useRef<BottomSheet>(null);
  const [activeProject, setActiveProject] = useState<ProjectTypes>();
  const infoModalRef = useRef<UserInfoModalHandle>(null);
  const shareUserRef = useRef<ShareConfirmHandle>(null);

  const _handleShareUser = useCallback((item) => {
    shareUserRef.current?.openModal(item);
  }, [shareUserRef, activeProject]);

  const _handleSelectUser = useCallback((item) => {
    infoModalRef.current?.toggleVisibility(item);
  }, [])

  const handleModalState = useCallback((item) => {
    setActiveProject(item);
    setDeleteModalVisible(true);
  }, [])

  const _appendData = useCallback((data) => {
    setFilteredProjects((prevData) => [...prevData, data]);
  }, [])

  const changeListView = useCallback(() => {
    if (listHorizontal) {
      setColumns(1);
    }
    setListHorizontal(!listHorizontal)

  }, [listHorizontal])

  const onRefresh = useCallback(() => {
    setTimeout(() => {
      setRefreshing(true);
      refetch()
        .then(() =>
          setRefreshing(false));
    }, 1000);
  }, []);

  const handleSearch = (text) => {
    const formattedQuery = text.toLowerCase();
    setQuery(text);
    const filteredData = filter(projectData, project => {
      return contains(project, formattedQuery);
    });
    setFilteredProjects(filteredData);
  };

  const contains = ({ title, description }, query: string) => {
    if (title.toLowerCase().includes(query) || description.toLowerCase().includes(query)) {
      return true;
    }
    return false;
  };

  const handlePresentModalPress = useCallback(() => {
    bottomDrawerRef.current?.present();
  }, []);

  return (
    <DefaultScreen backButton style={{ flex: 1, paddingLeft: 0, paddingRight: Platform.OS != 'web' ? undefined : 50 }} scrollDisabled searchBar={{
      callback: handleSearch, key: 'projectSearch', placeHolder: 'Search Campaigns', customIcons: [{
        icon: listHorizontal ? <FontAwesome5 name="th-list" size={Platform.select({ web: 24, default: 18 })} color="black" /> : <SimpleLineIcons name="grid" size={Platform.select({ web: 24, default: 14 })} color="white" />,
        function: changeListView,
        functionName: 'Change View',

      }]
    }}>
      <View style={{ flexDirection: 'column-reverse', flex: 1, alignSelf: "stretch" }}>
        <View style={{ flex: 10 }}>
          <FlatList
            key={listHorizontal ? 'horizontal' + columns : 'vertical' + columns}
            data={data}
            horizontal={listHorizontal}
            nestedScrollEnabled
            maxToRenderPerBatch={4}
            windowSize={6}
            contentContainerStyle={{ paddingBottom: listHorizontal ? 0 : 150, maxHeight: listHorizontal ? "100%" : undefined }}
            numColumns={listHorizontal ? undefined : columns}
            showsVerticalScrollIndicator={false}
            indicatorStyle='black'
            renderItem={({ item, index }) => (
              <PreviousCampaignItem
                navigation={navigation}
                item={item}
                index={index}
                horizontal={listHorizontal}
                _handleModalState={handleModalState}
                _handleSelectUser={_handleSelectUser}
                _handleShareUser={_handleShareUser}
              />
            )}
            ListEmptyComponent={(
              <View style={{ flex: 1, alignSelf: 'center', alignContent: 'center', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {
                  query != '' ?
                    <View style={[styles.noteContainer, { flex: 1, margin: 50, padding: 5, paddingBottom: 20 }]}>
                      <AnimatedLottieView source={require('@assets/animations/88006-warning-yellow.json')} autoPlay loop={false} style={{ height: 100, width: 100, flex: 1 }} />
                      <Text style={{ flex: 1 }}>No items found matching query</Text>
                    </View>
                    :
                    <View style={{ gap: 30, margin: 30, left: -25 }}>
                      <GlobalTutorial customHeight={400} customWidth={350} carouselData={newCarouselData} />
                    </View>
                }
              </View>
            )}
            ListFooterComponentStyle={{ alignSelf: 'center', maxWidth: '100%', justifyContent: 'center', paddingBottom: 50 }}
            style={{ marginVertical: 10, flex: 3, alignSelf: 'stretch', }}
            refreshControl={listHorizontal ? null : <RefreshControl refreshing={refresh} onRefresh={onRefresh} />}
            scrollEnabled={true}
            keyExtractor={item => item._id} />
        </View>
      </View>
    </DefaultScreen>
  )
}
export default PreviousCampaigns;